import React, { useState, useEffect } from 'react';
import { ArrowRight, User, Mail, Briefcase } from 'lucide-react';

interface UserInfoFormProps {
  initialData?: {
    firstName: string;
    lastName: string;
    preferredName?: string;
    email: string;
    jobTitle: string;
  };
  userEmail: string;
  userFirstName?: string;
  userLastName?: string;
  onSubmit: (data: {
    firstName: string;
    lastName: string;
    preferredName?: string;
    email: string;
    jobTitle: string;
  }) => void;
  isLoading: boolean;
}

export default function UserInfoForm({ 
  initialData, 
  userEmail,
  userFirstName,
  userLastName, 
  onSubmit, 
  isLoading 
}: UserInfoFormProps) {
  const [formData, setFormData] = useState({
    firstName: initialData?.firstName || userFirstName || '',
    lastName: initialData?.lastName || userLastName || '',
    preferredName: initialData?.preferredName || '',
    email: initialData?.email || userEmail,
    jobTitle: initialData?.jobTitle || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Update email if userEmail changes
  useEffect(() => {
    if (userEmail && !formData.email) {
      setFormData(prev => ({ ...prev, email: userEmail }));
    }
  }, [userEmail, formData.email]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = 'Job title is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        preferredName: formData.preferredName.trim() || undefined,
        email: formData.email.trim(),
        jobTitle: formData.jobTitle.trim(),
      });
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-6">
        <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center mx-auto mb-3">
          <User className="h-6 w-6 text-blue-600" />
        </div>
        <p className="text-muted-foreground">
          Let's start by getting to know you. This information will be used to create your staff profile.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* First Name */}
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium mb-1">
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            id="firstName"
            type="text"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.firstName ? 'border-red-500' : 'border-input'
            }`}
            placeholder="Enter your first name"
            disabled={isLoading}
          />
          {errors.firstName && (
            <p className="text-sm text-red-500 mt-1">{errors.firstName}</p>
          )}
        </div>

        {/* Last Name */}
        <div>
          <label htmlFor="lastName" className="block text-sm font-medium mb-1">
            Last Name <span className="text-red-500">*</span>
          </label>
          <input
            id="lastName"
            type="text"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.lastName ? 'border-red-500' : 'border-input'
            }`}
            placeholder="Enter your last name"
            disabled={isLoading}
          />
          {errors.lastName && (
            <p className="text-sm text-red-500 mt-1">{errors.lastName}</p>
          )}
        </div>
      </div>

      {/* Preferred Name */}
      <div>
        <label htmlFor="preferredName" className="block text-sm font-medium mb-1">
          Preferred Name (Optional)
        </label>
        <input
          id="preferredName"
          type="text"
          value={formData.preferredName}
          onChange={(e) => handleInputChange('preferredName', e.target.value)}
          className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="What would you like to be called?"
          disabled={isLoading}
        />
        <p className="text-sm text-muted-foreground mt-1">
          If different from your first name
        </p>
      </div>

      {/* Email Address */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium mb-1 flex items-center gap-2">
          <Mail className="h-4 w-4" />
          Email Address <span className="text-red-500">*</span>
        </label>
        <input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 ${
            errors.email ? 'border-red-500' : 'border-input'
          }`}
          placeholder="your.email@example.com"
          disabled={true} // Always disabled as it comes from Clerk
        />
        {errors.email && (
          <p className="text-sm text-red-500 mt-1">{errors.email}</p>
        )}
        <p className="text-sm text-muted-foreground mt-1">
          This email is linked to your login account and cannot be changed here
        </p>
      </div>

      {/* Job Title */}
      <div>
        <label htmlFor="jobTitle" className="block text-sm font-medium mb-1 flex items-center gap-2">
          <Briefcase className="h-4 w-4" />
          Job Title <span className="text-red-500">*</span>
        </label>
        <input
          id="jobTitle"
          type="text"
          value={formData.jobTitle}
          onChange={(e) => handleInputChange('jobTitle', e.target.value)}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.jobTitle ? 'border-red-500' : 'border-input'
          }`}
          placeholder="e.g., Marketing Manager, Software Developer"
          disabled={isLoading}
        />
        {errors.jobTitle && (
          <p className="text-sm text-red-500 mt-1">{errors.jobTitle}</p>
        )}
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-4">
        <button
          type="submit"
          disabled={isLoading}
          className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next: Organization Details
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </form>
  );
}
